import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import apiRequest from '../utils/api';

interface DashboardStats {
  totalOrders: number;
  pendingOrders: number;
  inprogressOrders: number;
  completedOrders: number;
  pauseOrders: number;
  refundOrders: number;
  waitRefundOrders: number;
  specialOrders: number;
  cancelRequestedOrders: number;
  totalRevenue: number;
  todayOrders: number;
  todayRevenue: number;
}

interface RecentOrder {
  idSmm: number;
  user: string;
  provider: string;
  serviceId: number;
  quantity: number;
  status: string;
  totalPriceSystem: number;
  created: string;
}

const Dashboard: React.FC = () => {
  const { hasAnyPermission, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [stats, setStats] = useState<DashboardStats>({
    totalOrders: 0,
    pendingOrders: 0,
    inprogressOrders: 0,
    completedOrders: 0,
    pauseOrders: 0,
    refundOrders: 0,
    waitRefundOrders: 0,
    specialOrders: 0,
    cancelRequestedOrders: 0,
    totalRevenue: 0,
    todayOrders: 0,
    todayRevenue: 0,
  });
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch dashboard summary with correct stats
      const timestamp = new Date().getTime();
      const dashboardResponse = await apiRequest(`/api/dashboard/summary?_t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      
      if (dashboardResponse.ok) {
        const dashboardData = await dashboardResponse.json();
        
        // Set recent orders
        setRecentOrders(dashboardData.recentOrders || []);

        // Set accurate stats from backend calculation
        setStats({
          totalOrders: dashboardData.totalOrders || 0,
          pendingOrders: dashboardData.pendingOrders || 0,
          inprogressOrders: dashboardData.inprogressOrders || 0,
          completedOrders: dashboardData.completedOrders || 0,
          pauseOrders: dashboardData.pauseOrders || 0,
          refundOrders: dashboardData.refundOrders || 0,
          waitRefundOrders: dashboardData.waitRefundOrders || 0,
          specialOrders: dashboardData.specialOrders || 0,
          cancelRequestedOrders: dashboardData.cancelRequestedOrders || 0,
          totalRevenue: dashboardData.totalRevenue || 0,
          todayOrders: dashboardData.todayOrders || 0,
          todayRevenue: dashboardData.todayRevenue || 0,
        });
      } else {
        const errorText = await dashboardResponse.text();
        console.error('Dashboard API error:', dashboardResponse.status, errorText);
        throw new Error(`Dashboard API returned ${dashboardResponse.status}: ${errorText}`);
      }
      setLastRefresh(new Date());
    } catch (error: any) {
      const errorMessage = error.message || 'Unknown error';
      setError(`Không thể tải dữ liệu dashboard: ${errorMessage}`);
      console.error('Dashboard error details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    // Đồng bộ với Orders: hiển thị UTC dd/MM/yyyy HH:mm:ss
    const dt = new Date(dateString);
    const pad = (n: number) => String(n).padStart(2, '0');
    const d = pad(dt.getUTCDate());
    const m = pad(dt.getUTCMonth() + 1);
    const y = dt.getUTCFullYear();
    const hh = pad(dt.getUTCHours());
    const mm = pad(dt.getUTCMinutes());
    const ss = pad(dt.getUTCSeconds());
    return `${d}/${m}/${y} ${hh}:${mm}:${ss}`;
  };

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      pending: { backgroundColor: '#fef3c7', color: '#d97706', text: 'Chờ xử lý' },
      inprogress: { backgroundColor: '#dbeafe', color: '#2563eb', text: 'Đang xử lý' },
      completed: { backgroundColor: '#dcfce7', color: '#16a34a', text: 'Hoàn thành' },
      pause: { backgroundColor: '#f3f4f6', color: '#6b7280', text: 'Tạm dừng' },
      refund: { backgroundColor: '#fecaca', color: '#dc2626', text: 'Hoàn tiền' },
      wait_refund: { backgroundColor: '#fed7d7', color: '#dc2626', text: 'Chờ hoàn tiền' },
      special: { backgroundColor: '#e0e7ff', color: '#3730a3', text: 'Đặc biệt' },
    };

    const style = statusStyles[status as keyof typeof statusStyles] || statusStyles.pending;

    return (
      <span style={{
        ...style,
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500'
      }}>
        {style.text}
      </span>
    );
  };

  const StatCard: React.FC<{ 
    title: string; 
    value: string; 
    change?: string;
    changeColor?: string;
    icon: string;
    bgColor: string;
  }> = ({ title, value, change, changeColor = '#16a34a', icon, bgColor }) => (
    <div style={{
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #f1f5f9'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ 
            fontSize: '14px', 
            color: '#64748b', 
            margin: '0 0 8px 0',
            fontWeight: '500'
          }}>
            {title}
          </p>
          <p style={{ 
            fontSize: '28px', 
            fontWeight: '700', 
            color: '#1e293b', 
            margin: '0 0 4px 0' 
          }}>
            {value}
          </p>
          {change && (
            <p style={{ 
              fontSize: '12px', 
              color: changeColor, 
              margin: 0,
              fontWeight: '500'
            }}>
              {change}
            </p>
          )}
        </div>
        <div style={{
          width: '48px',
          height: '48px',
          backgroundColor: bgColor,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px'
        }}>
          {icon}
        </div>
      </div>
    </div>
  );

  // Check if user has permission for dashboard module
  if (!hasAnyPermission('dashboard')) {
    return (
      <Layout currentPage="dashboard">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{ fontSize: '48px' }}>🔒</div>
          <div style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#374151',
            textAlign: 'center'
          }}>
            Không có quyền truy cập
          </div>
          <div style={{ 
            fontSize: '14px', 
            color: '#6b7280',
            textAlign: 'center'
          }}>
            Bạn không có quyền truy cập module này. Vui lòng liên hệ quản trị viên.
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout currentPage="dashboard">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '400px' 
        }}>
          <div className="loading-spinner" style={{ width: '40px', height: '40px' }}></div>
        </div>
      </Layout>
    );
  }


  return (
    <Layout currentPage="dashboard">
      <div style={{ 
        width: '100%',
        maxWidth: '100%',
        padding: '0'
      }}>
        {error && (
          <div style={{
            backgroundColor: '#fecaca',
            border: '1px solid #f87171',
            color: '#dc2626',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <StatCard
            title="Tổng đơn hàng"
            value={stats.totalOrders.toLocaleString()}
            icon="📦"
            bgColor="#dbeafe"
          />
          <StatCard
            title="Chờ xử lý"
            value={stats.pendingOrders.toLocaleString()}
            icon="⏳"
            bgColor="#fef3c7"
          />
          <StatCard
            title="Đang xử lý"
            value={stats.inprogressOrders.toLocaleString()}
            icon="🔄"
            bgColor="#dbeafe"
          />
          <StatCard
            title="Hoàn thành"
            value={stats.completedOrders.toLocaleString()}
            icon="✅"
            bgColor="#dcfce7"
          />
          <StatCard
            title="Tạm dừng"
            value={stats.pauseOrders.toLocaleString()}
            icon="⏸️"
            bgColor="#f3f4f6"
          />
          <StatCard
            title="Hoàn tiền"
            value={stats.refundOrders.toLocaleString()}
            icon="💸"
            bgColor="#fecaca"
          />
          <StatCard
            title="Chờ hoàn tiền"
            value={stats.waitRefundOrders.toLocaleString()}
            icon="⏱️"
            bgColor="#fed7d7"
          />
          <StatCard
            title="Đã gửi yêu cầu hủy"
            value={stats.cancelRequestedOrders.toLocaleString()}
            icon="🚫"
            bgColor="#fef3c7"
          />
          <StatCard
            title="Đặc biệt"
            value={stats.specialOrders.toLocaleString()}
            icon="⭐"
            bgColor="#e0e7ff"
          />
          <StatCard
            title="Doanh thu"
            value={formatCurrency(stats.totalRevenue)}
            icon="💰"
            bgColor="#f0fdf4"
          />
        </div>

        {/* Today's Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <StatCard
            title="Đơn hàng hôm nay"
            value={stats.todayOrders.toLocaleString()}
            change="📈 Hôm nay"
            icon="🗓️"
            bgColor="#fce7f3"
          />
          <StatCard
            title="Doanh thu hôm nay"
            value={formatCurrency(stats.todayRevenue)}
            change="💹 Hôm nay"
            icon="📊"
            bgColor="#f0fdf4"
          />
        </div>

        {/* Recent Orders */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #f1f5f9'
        }}>
          <div style={{
            padding: window.innerWidth <= 768 ? '16px' : '20px',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <h3 style={{ 
              fontSize: window.innerWidth <= 768 ? '16px' : '18px', 
              fontWeight: '600', 
              color: '#1e293b', 
              margin: 0 
            }}>
              Đơn hàng gần đây
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button 
                onClick={fetchDashboardData}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#475569',
                  cursor: 'pointer'
                }}
              >
                🔄 Làm mới
              </button>
              {lastRefresh && (
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  Cập nhật: {formatDate(lastRefresh.toISOString())}
                </span>
              )}
            </div>
          </div>

          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              minWidth: window.innerWidth <= 768 ? '800px' : '100%'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>ID</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Người dùng</th>
                  {isAdmin && (
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Nhà cung cấp</th>
                  )}
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Dịch vụ</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Số lượng</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Trạng thái</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Tổng tiền</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Ngày tạo</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order, index) => (
                  <tr key={order.idSmm} style={{
                    borderBottom: index === recentOrders.length - 1 ? 'none' : '1px solid #f1f5f9'
                  }}>
                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                      #{order.idSmm}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#475569' }}>
                      {order.user}
                    </td>
                    {isAdmin && (
                      <td style={{ padding: '12px', fontSize: '14px', color: '#475569' }}>
                        {order.provider}
                      </td>
                    )}
                    <td style={{ padding: '12px', fontSize: '14px', color: '#475569' }}>
                      {order.serviceId}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#475569' }}>
                      {order.quantity?.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {getStatusBadge(order.status)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                      {formatCurrency(parseFloat(order.totalPriceSystem?.toString() || '0'))}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#64748b' }}>
                      {formatDate(order.created)}
                    </td>
                  </tr>
                ))}
                {recentOrders.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ 
                      padding: '40px', 
                      textAlign: 'center', 
                      color: '#94a3b8',
                      fontSize: '14px'
                    }}>
                      Chưa có đơn hàng nào
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;